// Thuật toán tính giá POS (đặc tả triển khai mục 2.5, 2.6) — hàm thuần, không phụ thuộc UI
import type { CartLine, DiscountType, OrderTotals, PricedLine, Promotion, Voucher } from './data';

export interface PricingContext {
  branchId: string;
  promotions: Promotion[];
  orderDiscount: { type: DiscountType; value: number };
  voucher: Voucher | null;
  roundingUnit: number;
  now?: Date;
}

export interface PricingResult {
  lines: PricedLine[];
  totals: OrderTotals;
  /** P2: % chiết khấu thủ công so với tạm tính sau KM tự động */
  manualPercent: number;
  voucherError?: string;
}

/** P1: % làm tròn về đồng; số tiền không vượt quá base */
export const applyDiscount = (base: number, type: DiscountType, value: number) => {
  const v = Math.max(0, Number(value) || 0);
  if (base <= 0) return 0;
  if (type === '%') return Math.round(base * Math.min(v, 100) / 100);
  return Math.min(Math.round(v), base);
};

export const roundHalfUp = (raw: number, unit: number) => (unit > 1 ? Math.floor(raw / unit + 0.5) * unit : Math.round(raw));

const isActive = (p: Promotion, branchId: string, now: Date) =>
  p.enabled &&
  new Date(p.startAt) <= now &&
  now <= new Date(p.endAt) &&
  (p.branchIds === 'all' || p.branchIds.includes(branchId));

export const promotionStatus = (p: Promotion, now = new Date()): 'off' | 'upcoming' | 'running' | 'ended' => {
  if (!p.enabled) return 'off';
  if (now < new Date(p.startAt)) return 'upcoming';
  if (now > new Date(p.endAt)) return 'ended';
  return 'running';
};

/** P3: lấy chương trình ưu tiên cao nhất trong nhóm không cộng dồn, cộng thêm các chương trình cộng dồn */
const pickPromotions = (candidates: Promotion[]) => {
  const exclusive = candidates.filter(p => !p.stackable).sort((a, b) => b.priority - a.priority)[0];
  return [...(exclusive ? [exclusive] : []), ...candidates.filter(p => p.stackable)];
};

const promoAmount = (p: Promotion, base: number, qty: number) => {
  const raw = p.valueType === '%' ? Math.round(base * p.value / 100) : p.value * qty;
  return Math.min(base, p.maxDiscount ? Math.min(raw, p.maxDiscount) : raw);
};

export const itemPromotionsFor = (itemId: string, ctx: Pick<PricingContext, 'branchId' | 'promotions' | 'now'>) => {
  const now = ctx.now || new Date();
  return pickPromotions(ctx.promotions.filter(p => p.type === 'item' && p.itemIds.includes(itemId) && isActive(p, ctx.branchId, now)));
};

export type VoucherCheck = { ok: true; voucher: Voucher } | { ok: false; error: string };

/** BR8.3: kiểm tra voucher (chưa xét đơn tối thiểu — phần đó tính trong calcOrder) */
export const checkVoucher = (code: string, vouchers: Voucher[], now = new Date()): VoucherCheck => {
  const normalized = code.trim().toUpperCase().replace(/\s+/g, '');
  if (!normalized) return { ok: false, error: 'Vui lòng nhập mã' };
  const v = vouchers.find(x => x.code === normalized);
  if (!v || !v.enabled) return { ok: false, error: 'Mã không tồn tại' };
  if (new Date(v.expiresAt) < now) return { ok: false, error: 'Mã đã hết hạn' };
  if (v.used >= v.usageLimit) return { ok: false, error: 'Mã đã hết lượt sử dụng' };
  return { ok: true, voucher: v };
};

/** Phân bổ số tiền về các dòng theo tỷ lệ; dòng cuối nhận phần dư cho khớp tổng */
const allocate = (amount: number, weights: number[]) => {
  const sum = weights.reduce((s, w) => s + w, 0);
  if (!amount || !sum) return weights.map(() => 0);
  let used = 0;
  return weights.map((w, i) => {
    if (i === weights.length - 1) return amount - used;
    const part = Math.round(amount * w / sum);
    used += part;
    return part;
  });
};

export function calcOrder(cart: CartLine[], ctx: PricingContext): PricingResult {
  const now = ctx.now || new Date();
  const fmt = (n: number) => new Intl.NumberFormat('vi-VN').format(n);

  // Bước 1–3: giá niêm yết → KM theo dòng → chiết khấu dòng
  const stage = cart.map(line => {
    const qty = Math.max(0, Math.floor(Number(line.qty) || 0));
    const gross = line.listPrice * qty;
    const promos = itemPromotionsFor(line.itemId, { ...ctx, now });
    let promo = 0;
    promos.forEach(p => { promo += promoAmount(p, gross - promo, qty); });
    const afterPromo = gross - promo;
    const lineDiscountAmount = applyDiscount(afterPromo, line.discountType, line.discountValue);
    return {
      ...line,
      qty,
      gross,
      promoAmount: promo,
      promoName: promos.map(p => p.name).join(' + ') || undefined,
      lineDiscountAmount,
      afterLine: afterPromo - lineDiscountAmount,
    };
  });

  const subtotal = stage.reduce((s, l) => s + l.gross, 0);
  const itemPromo = stage.reduce((s, l) => s + l.promoAmount, 0);
  const lineDiscount = stage.reduce((s, l) => s + l.lineDiscountAmount, 0);
  const after = stage.reduce((s, l) => s + l.afterLine, 0);

  // Bước 4: KM theo đơn
  const orderPromos = pickPromotions(
    ctx.promotions.filter(p => p.type === 'order' && isActive(p, ctx.branchId, now) && after >= (p.minOrder || 0)),
  );
  let orderPromo = 0;
  orderPromos.forEach(p => { orderPromo += promoAmount(p, after - orderPromo, 1); });
  const rest1 = after - orderPromo;

  // Bước 5: chiết khấu đơn thủ công
  const orderDiscount = applyDiscount(rest1, ctx.orderDiscount.type, ctx.orderDiscount.value);
  const rest2 = rest1 - orderDiscount;

  // Bước 6: voucher
  let voucher = 0;
  let voucherError: string | undefined;
  if (ctx.voucher) {
    if (rest2 < ctx.voucher.minOrder) {
      voucherError = `Đơn chưa đạt tối thiểu ${fmt(ctx.voucher.minOrder)}đ`;
    } else {
      const v = ctx.voucher;
      const rawVoucher = v.valueType === '%' ? Math.round(rest2 * v.value / 100) : v.value;
      voucher = Math.min(rest2, v.maxDiscount ? Math.min(rawVoucher, v.maxDiscount) : rawVoucher);
    }
  }
  const raw = Math.max(0, rest2 - voucher);

  // Bước 7: làm tròn
  const total = roundHalfUp(raw, ctx.roundingUnit);
  const rounding = total - raw;

  // Phân bổ giảm cấp đơn (và phần làm tròn) về dòng để tổng thực trả các dòng = tổng đơn
  const parts = allocate(orderPromo + orderDiscount + voucher - rounding, stage.map(l => l.afterLine));
  const lines: PricedLine[] = stage.map((l, i) => ({
    ...l,
    allocated: parts[i],
    net: l.afterLine - parts[i],
    refundedQty: 0,
    refundedAmount: 0,
  }));

  const afterAuto = subtotal - itemPromo;
  const manualPercent = afterAuto > 0 ? ((lineDiscount + orderDiscount) / afterAuto) * 100 : 0;

  return {
    lines,
    totals: {
      subtotal,
      itemPromo,
      lineDiscount,
      orderPromo,
      orderPromoName: orderPromos.map(p => p.name).join(' + ') || undefined,
      orderDiscount,
      voucher,
      rounding,
      total,
    },
    manualPercent,
    voucherError,
  };
}

/** 2.6: số tiền được hoàn cho `qty` đơn vị của một dòng */
export const refundLineAmount = (line: PricedLine, qty: number) => {
  const remaining = line.qty - line.refundedQty;
  if (qty <= 0) return 0;
  if (qty >= remaining) return line.net - line.refundedAmount;
  return Math.round((line.net / line.qty) * qty);
};
